import React, { useEffect, useRef, useState } from 'react';
import { updateUserSettings } from '../../services/api';
import { captureEvent } from '../../services/analytics';
import { getApiErrorMessage } from '../../utils/errorUtils';
import { showErrorToastFullScreen, showSuccessToastFullScreen } from '../../utils/toastUtils';
import { takeAutoApplyResume } from '../../utils/gmailReturn';
import { useAutoApplyGate } from '../../hooks/useAutoApplyGate';
import { UserSettings } from '../../types';
import AutoApplyActivationModal, { AUTO_APPLY_THRESHOLD_LABELS } from './AutoApplyActivationModal';
import { AutoApplyGateVariant } from './AutoApplyGmailGateModal';
import css from './AutoApplySection.module.scss';

interface AutoApplySectionProps {
  // Null until the parent's single GET resolves, and null for good if it
  // failed. Either way this card must not offer a control that saves.
  settings: UserSettings | null;
  // True only while that GET is in flight — tells the two null states apart.
  loading?: boolean;
  onSaved: (settings: UserSettings) => void;
  // Set by the ?autoApply=setup deep link in the nudge email: land with the
  // activation modal already open.
  autoOpenSetup?: boolean;
  // Fired once the deep link has been honoured, so the parent can clear the
  // query param.
  onAutoOpened?: () => void;
  // Gmail connection state, owned by the Settings page (one GET per page load).
  // All three are needed, not just canSend: canSend initialises to `true` so a
  // legacy payload without the field reads as usable, which means `!gmailCanSend`
  // alone reports a healthy Gmail all through the loading window and for anyone
  // with no account at all. Connectedness and loadedness carry the rest.
  gmailConnected?: boolean;
  gmailCanSend?: boolean;
  gmailLoading?: boolean;
}

/**
 * Auto-Apply card (design §10). OFF: explainer + primary setup CTA. ON:
 * status line + Manage (reopens the modal prefilled) + one-click Turn off
 * (PUTs only autoApplyEnabled:false — no confirm by design).
 *
 * Until real settings arrive the card is inert: the activation modal seeds
 * once on open and PUTs all five fields, so a CTA offered over a null
 * settings object would save empty exclusion lists over the user's real
 * ones — the only filter standing between Auto-Apply and their employer.
 */
const AutoApplySection: React.FC<AutoApplySectionProps> = ({
  settings,
  onSaved,
  loading,
  autoOpenSetup,
  onAutoOpened,
  gmailConnected,
  gmailCanSend,
  gmailLoading,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [turningOff, setTurningOff] = useState(false);
  const autoOpenedRef = useRef(false);
  const resumeHandledRef = useRef(false);

  const { requestSetup, requestRepair, showGateAfterSaveError, probing, gate } = useAutoApplyGate(
    () => setModalOpen(true),
    'settings'
  );

  // Tri-state, never a bare boolean. `unknown` is a real state here twice over:
  // the Gmail GET resolves after mount, AND a host may not supply these props at
  // all. Both must read as unknown — collapsing either into "unusable" tells a
  // perfectly healthy user we cannot send, which is the same false status line
  // this card exists to stop showing, just inverted. Only an explicit
  // gmailConnected from the host is allowed to drive the warning.
  const gmailStatus: 'unknown' | 'usable' | 'unusable' =
    gmailLoading || gmailConnected === undefined
      ? 'unknown'
      : gmailConnected && gmailCanSend !== false
      ? 'usable'
      : 'unusable';

  useEffect(() => {
    // Gated on settings !== null for the same reason the card is inert: the
    // modal seeds from settings on open and PUTs all five fields, so opening
    // it early would overwrite the user's real exclusion lists with empty
    // ones. Fires once — clearing the query param prevents a refresh or
    // back-navigation from reopening it, and the ref keeps a settings refetch
    // from reopening a modal the user dismissed.
    if (!autoOpenSetup || !settings || autoOpenedRef.current) return;
    autoOpenedRef.current = true;
    // Through the gate, not straight into the form. The nudge only mails users
    // who could send WHEN IT WAS SENT — they may have disconnected in the days
    // since, and landing them in a two-step form that cannot save is the same
    // dead end this gate exists to remove.
    requestSetup();
    captureEvent('auto_apply_setup_opened', { source: 'nudge_email' });
    onAutoOpened?.();
  }, [autoOpenSetup, settings, onAutoOpened, requestSetup]);

  // Coming back from the Gmail round trip. Waits for BOTH the settings GET and
  // the Gmail GET to settle before consuming the flag: it is one-shot, so
  // reading it against an unresolved status would spend it on a decision made
  // from a default.
  useEffect(() => {
    if (resumeHandledRef.current) return;
    if (!settings || gmailStatus === 'unknown') return;
    resumeHandledRef.current = true;

    const intent = takeAutoApplyResume();
    if (!intent) return;

    // Consumed either way. On the unusable path the user connected without
    // granting send, and the Settings page's own "sending isn't enabled" alert
    // is the right thing to leave them looking at — not a setup modal that
    // cannot save.
    if (gmailStatus !== 'usable') return;

    captureEvent('auto_apply_setup_resumed_after_gmail', { intent });
    if (intent === 'setup') {
      autoOpenedRef.current = true;
      setModalOpen(true);
    } else {
      showSuccessToastFullScreen('Gmail connected — Auto-Apply can send again.');
    }
  }, [settings, gmailStatus]);

  const enabled = settings?.autoApplyEnabled === true;
  const cap = settings?.autoApplyDailyCap ?? 5;
  const thresholdLabel =
    AUTO_APPLY_THRESHOLD_LABELS[settings?.autoApplyThreshold ?? 'perfect_fit'];
  // Auto-Apply says it is on while nothing can go out. The card must say so
  // here rather than show a green dot — the whole reason a production user sat
  // broken for three days is that this line kept reporting success.
  const stalled = enabled && gmailStatus === 'unusable';
  const stalledVariant: AutoApplyGateVariant = gmailConnected ? 'fix' : 'connect';

  const handleTurnOff = async () => {
    if (turningOff) return;
    setTurningOff(true);
    try {
      const updated = await updateUserSettings({ autoApplyEnabled: false });
      onSaved(updated);
      showSuccessToastFullScreen('Auto-Apply turned off.');
      captureEvent('auto_apply_turned_off', {});
    } catch (error) {
      showErrorToastFullScreen(getApiErrorMessage(error));
    } finally {
      setTurningOff(false);
    }
  };

  if (!settings) {
    return (
      <section className={css.section}>
        <h2>Auto-Apply</h2>
        <div className={css.card} role="status">
          <p className={css.explainer}>
            {loading
              ? 'Loading your Auto-Apply settings…'
              : "We couldn't load your Auto-Apply settings. Refresh the page to try again."}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className={css.section}>
      <h2>Auto-Apply</h2>
      <div className={css.card}>
        {enabled ? (
          <>
            {stalled ? (
              <p className={css.stalledLine}>
                <span className={css.stalledDot} aria-hidden="true" />
                {/* Names the problem; the button beside it names the fix, so this
                    must not repeat it. Kept short because THIS state shows a
                    third button, which squeezes the text column to roughly half
                    the width the healthy line gets — the longer wording ran to
                    four lines at 1920. */}
                {stalledVariant === 'fix'
                  ? "Auto-Apply is on, but Gmail hasn't allowed us to send."
                  : "Auto-Apply is on, but your Gmail isn't connected."}
              </p>
            ) : (
              <p className={css.statusLine}>
                <span className={css.statusDot} aria-hidden="true" />
                {/* "for X matches" stays: without it the threshold label dangles
                    and the reader has to infer what "Strong Alignment and up"
                    qualifies. This wraps to two lines at some widths, which is
                    fine — .stalledLine's text-wrap keeps it from stranding a
                    single word, which was the actual complaint. */}
                Auto-Apply is on — up to {cap}/day for {thresholdLabel} matches.
              </p>
            )}
            <div className={css.actions}>
              {stalled && (
                <button
                  type="button"
                  className={css.setupButton}
                  onClick={() => requestRepair(stalledVariant)}
                >
                  {stalledVariant === 'fix' ? 'Grant permission' : 'Connect Gmail'}
                </button>
              )}
              {/* Manage stays ungated: this row is already enabled, so the save
                  is not an off->on transition and the server does not block it.
                  Gating it would strand the stalled user away from the very
                  exclusions and cap that protect them. */}
              <button
                type="button"
                className={css.manageButton}
                onClick={() => setModalOpen(true)}
              >
                Manage
              </button>
              <button
                type="button"
                className={css.turnOffButton}
                onClick={handleTurnOff}
                disabled={turningOff}
              >
                {turningOff ? 'Turning off…' : 'Turn off'}
              </button>
            </div>
          </>
        ) : (
          <>
            <p className={css.explainer}>
              Auto-Apply sends your strongest matches for you — max {cap}/day, only at your
              chosen match level.
            </p>
            <div className={css.actions}>
              <button
                type="button"
                className={css.setupButton}
                onClick={requestSetup}
                disabled={probing}
              >
                {probing ? 'Checking…' : 'Set up Auto-Apply'}
              </button>
            </div>
          </>
        )}
      </div>

      <AutoApplyActivationModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        settings={settings}
        onSaved={onSaved}
        onGmailRequired={showGateAfterSaveError}
      />

      {gate}
    </section>
  );
};

export default AutoApplySection;
